import { useState, useEffect } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_ITEMS = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/aerial", label: "Aerial & Drone", testid: "nav-aerial" },
  { to: "/ground", label: "Ground", testid: "nav-ground" },
  { to: "/photography", label: "Photography", testid: "nav-photography" },
  { to: "/video-clips", label: "Video Clips", testid: "nav-video-clips" },
  { to: "/about", label: "About", testid: "nav-about" },
  { to: "/contact", label: "Contact", testid: "nav-contact" },
];

const SidebarContent = ({ onNavigate }) => (
  <div className="flex flex-col h-full">
    <div className="px-6 pt-8 pb-10">
      <NavLink
        to="/"
        onClick={onNavigate}
        data-testid="brand-link"
        className="block"
      >
        <div className="font-display text-[22px] leading-none text-white tracking-tight">
          HighwaysLens
        </div>
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#737373] mt-3">
          Captured with precision
        </div>
      </NavLink>
    </div>
    <nav className="flex flex-col gap-1 px-2">
      {NAV_ITEMS.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === "/"}
          onClick={onNavigate}
          data-testid={item.testid}
          className={({ isActive }) =>
            `hl-sidebar-link ${isActive ? "active" : ""}`
          }
        >
          <span className="font-display text-[14px] tracking-tight">
            {item.label}
          </span>
        </NavLink>
      ))}
    </nav>
    <div className="mt-auto px-6 pb-8 pt-10">
      <div className="text-[10px] uppercase tracking-[0.28em] text-[#737373] mb-3">
        Studio
      </div>
      <div className="text-[12px] text-[#A3A3A3] leading-relaxed">
        UK Highways · Construction<br />
        Photography & Film
      </div>
      <div className="text-[10px] text-[#525252] mt-6">
        © {new Date().getFullYear()} HighwaysLens
      </div>
    </div>
  </div>
);

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex bg-[#111111] hl-grain">
      {/* Desktop sidebar */}
      <aside
        data-testid="sidebar"
        className="hl-desktop-only fixed top-0 left-0 h-screen w-[220px] bg-[#0A0A0A] border-r border-[#1a1a1a] z-30"
      >
        <SidebarContent />
      </aside>

      {/* Mobile top bar */}
      <header className="hl-mobile-only fixed top-0 left-0 right-0 h-14 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-[#1a1a1a] z-40 flex items-center justify-between px-4">
        <NavLink to="/" className="font-display text-[18px] text-white" data-testid="brand-link-mobile">
          HighwaysLens
        </NavLink>
        <button
          onClick={() => setMobileOpen(true)}
          data-testid="mobile-menu-open"
          className="p-2 text-white"
          aria-label="Open menu"
        >
          <Menu size={22} />
        </button>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="hl-mobile-only fixed inset-0 z-50" data-testid="mobile-drawer">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-drawer-backdrop"
          />
          <div className="absolute top-0 left-0 h-full w-[260px] bg-[#0A0A0A] border-r border-[#1a1a1a] animate-in slide-in-from-left">
            <button
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-menu-close"
              className="absolute top-4 right-4 p-1 text-white"
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 lg:ml-[220px] pt-14 lg:pt-0 min-h-screen relative">
        <div className="hl-page-enter" key={location.pathname}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
